import type { QueryClient } from '@tanstack/svelte-query';
import { createQuery } from '@tanstack/svelte-query';
import { fromStore, get, toStore, type Readable } from 'svelte/store';
import type {
	GetEditionsCartQuery,
	GetEditionsCartQueryVariables,
} from '../../queries/__generated__/getEditionsCart.generated';
import { GetEditionsCartDocument } from '../../queries/__generated__/getEditionsCart.generated';
import { getEditionsCartQueryVariables } from '../getEditionsCartQueryVariables/getEditionsCartQueryVariables';
import { FeatureFlags } from '#lib/constants/feature-flags.js';
import { NONEXISTENT_PRODUCT_ID } from '#lib/features/checkout/constants/nonexistent-product-id.js';
import type { CartItems } from '#lib/features/checkout/utils/createCartItemsStore/index.js';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { getEditionsCart } from '#lib/websites/editions/api/getEditionsCart.js';
import { stringifyIds } from '#lib/websites/nft/utils/stringifyIds/stringifyIds.js';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import { getImageUrl } from '$global/utils/getImageUrl';

export const getEditionsCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>
) => {
	const cartItems$ = fromStore(cartItems);

	return createQuery(() => {
		const $cartItems = cartItems$.current;
		const ids = Object.keys($cartItems || {});
		const queryClientData = queryClient.getQueriesData({});

		const existingQueryResult = ids?.length
			? queryClientData?.find(([[key, _, variables]]) => {
					return (
						key === 'getEditionsCart' &&
						ids.every((id) =>
							(
								variables as GetEditionsCartQueryVariables
							)?.filter?.product?.id?.['_in']?.includes(id)
						)
					);
				})
			: null;

		// If there's already cache entry a query fetching more editions than the current one
		if (existingQueryResult?.[1]) {
			return {
				queryKey: getQueryKeys(
					GetEditionsCartDocument,
					getEditionsCartQueryVariables(ids)
				),
				queryFn: () => {
					return {
						Editions: (
							existingQueryResult?.[1] as GetEditionsCartQuery
						).Editions?.filter((edition) =>
							ids.includes(edition.product?.id as string)
						),
						Drop_Config: (existingQueryResult?.[1] as GetEditionsCartQuery)
							.Drop_Config,
					};
				},
			};
		}

		const queryIds = ids?.length ? ids : [NONEXISTENT_PRODUCT_ID];
		const variables = getEditionsCartQueryVariables(queryIds);

		// The query key is the GraphQL one on both paths, so the cache reuse above
		// and the layout's empty-cart seed keep working whichever path is live.
		return FeatureFlags.RestPages['Editions cart hydration']
			? {
					queryKey: getQueryKeys(GetEditionsCartDocument, variables),
					queryFn: async (): Promise<GetEditionsCartQuery> =>
						stringifyIds(await getEditionsCart(fetch, queryIds)),
				}
			: getQuery(GetEditionsCartDocument, variables);
	});
};

export const formatEditionsItems = (
	$editionsCartQuery: {
		data: GetEditionsCartQuery | undefined;
	},
	cartItems: Readable<CartItems | undefined>
) =>
	($editionsCartQuery.data?.Editions || []).map((edition) => {
		const allDefinedImage = edition.image ? edition.image : null;

		return {
			image: allDefinedImage
				? {
						width: allDefinedImage?.width || 0,
						height: allDefinedImage?.height || 0,
						url: `${getImageUrl(allDefinedImage?.filename_disk, '512px')}`,
					}
				: null,
			dropConfigId: $editionsCartQuery.data?.Drop_Config?.[0]?.id,
			name: edition?.name,
			code: edition?.code,
			line1: (edition?.artists || [])
				?.map((artist) => artist?.Artists_id?.name)
				?.join(', '),
			line2: `${edition?.code} | ${edition?.name}`,
			line3:
				edition.dimensions_description ||
				`${edition?.length_cm} x ${edition?.width_cm} CM`,
			productId: edition?.product?.id,
			id: edition?.id,
			quantity: get(cartItems)?.[edition?.product?.id as string]?.quantity || 0,
			currency: edition?.product?.currency,
			sku: edition?.product?.sku,
			price: edition?.product?.price,
			variant: null,
			groupId: edition?.series?.code,
			groupName: edition?.series?.name,
		};
	});

export const createEditionsCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>,
	defaultLoading?: boolean
) => {
	const editionsCartQuery = getEditionsCartQuery(queryClient, cartItems);

	return toStore(() => ({
		...editionsCartQuery,
		error: editionsCartQuery.error || null,
		data: editionsCartQuery.data || { Editions: [] },
		isLoading: (() => {
			if (!editionsCartQuery.data) {
				return typeof defaultLoading !== 'undefined' ? defaultLoading : false;
			}

			return editionsCartQuery.isPending;
		})(),
		subtotal: Object.values(get(cartItems) || {}).reduce(
			(accumulator, cartItem) => {
				if (!cartItem.quantity || !cartItem.price) {
					return accumulator;
				}

				return accumulator + cartItem.quantity * cartItem.price;
			},
			0
		),
		formattedItems: formatEditionsItems(editionsCartQuery, cartItems),
	}));
};
